// src/services/companionService.js
// Client rail for Sofia, the Socratic companion — thin wrapper over the DDD
// Knowledge Hub coaching endpoints. The KH functions are onRequest callables,
// so the caller's ID token rides the Authorization header. Since the auth-hub
// cutover the session is the HUB session (authHubApp), so the token is
// hub-audience — the first link in KH's verification chain. The app's own
// project stays as fallback for any legacy spoke session.
//
// What is distinctly Sofia's: every askCoach carries a compact summary of the
// positions the reader has recorded at the course's forks, so she can test
// today's argument against the fork they answered last week.
//
// Australian English throughout.
import { getFunctions, httpsCallableFromURL } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { authHubApp } from './firebase';
import { POSITION_POINTS } from '../data/positionPoints';

const APP_ID = 'profound-philosophy';
const BASE_URL = 'https://australia-southeast1-ddd-knowledge-hub.cloudfunctions.net';

const URLS = {
  askCoach: `${BASE_URL}/askCoach`,
  getProfile: `${BASE_URL}/getCoachingProfile`,
  updateProfile: `${BASE_URL}/updateCoachingProfile`,
  deleteProfile: `${BASE_URL}/deleteCoachingProfile`,
  exportData: `${BASE_URL}/exportCoachingData`,
  startSession: `${BASE_URL}/startCoachingSession`,
  endSession: `${BASE_URL}/endCoachingSession`,
  forgetSession: `${BASE_URL}/forgetCoachingSession`,
};

function getCallable(url) {
  try {
    // Hub-first: the hub session is the sign-in, so its token must ride the
    // Authorization header. Fall back to the default app for any legacy
    // own-project session.
    const app = authHubApp || getApp();
    return httpsCallableFromURL(getFunctions(app, 'australia-southeast1'), url);
  } catch {
    return null;
  }
}

// Summarise the reader's recorded positions for Sofia's companion mode.
// The hub whitelists `positionsSummary` and caps it at 700 characters, so the
// summary is kept compact: "Fork title: choice; …".
export const buildPositionsContext = () => {
  try {
    const positions = JSON.parse(localStorage.getItem('philosophicalPositions')) || {};
    return Object.entries(positions)
      .map(([forkId, a]) => {
        const point = POSITION_POINTS.find(p => p.id === forkId);
        const option = point?.options.find(o => o.id === a.choice);
        return point && option ? `${point.title}: ${option.label}` : null;
      })
      .filter(Boolean)
      .join('; ')
      .slice(0, 700);
  } catch {
    return '';
  }
};

// Error copy in Sofia's register — precise, warm, never curt.
function mapError(error) {
  const code = error?.code;
  const details = error?.customData || error?.details || {};
  if (code === 'functions/unauthenticated') {
    return { success: false, error: 'signin-required', response: 'You’ll need to sign in before we can talk.' };
  }
  if (code === 'functions/failed-precondition') {
    return { success: false, error: 'consent-required', response: 'There’s one thing to agree to before we begin.' };
  }
  if (code === 'functions/resource-exhausted') {
    return {
      success: false,
      error: 'cap-exceeded',
      dailyCapExceeded: !!details.dailyCapExceeded,
      appCapExceeded: !!details.appCapExceeded,
      tier: details.tier,
      dailyLimit: details.dailyLimit,
      upgradeUrl: details.upgradeUrl,
      response: details.appCapExceeded
        ? 'Sofia has a full courtyard at the moment. Please come back a little later.'
        : details.tier === 'free'
          ? 'That’s the last of today’s conversations on the free plan. It resets each day at midnight UTC, and the questions will keep.'
          : 'You’ve reached today’s limit. It resets each day at midnight UTC.',
    };
  }
  return {
    success: false,
    error: error?.message || 'unknown',
    response: 'I’m having trouble connecting just now. Please try again in a moment.',
  };
}

class CompanionService {
  constructor() {
    this.conversationHistory = [];
    this.sessionId = null;
  }

  resetSession() {
    this.conversationHistory = [];
    this.sessionId = null;
  }

  // Module singleton, so the thread survives route changes, but not a reload,
  // and not a remount against a fresh import. snapshot/restore let the page
  // carry the thread across both (see CompanionPage's sessionStorage rail).
  snapshot() {
    return { sessionId: this.sessionId, conversationHistory: this.conversationHistory };
  }

  restore({ sessionId, conversationHistory } = {}) {
    if (sessionId) this.sessionId = sessionId;
    if (Array.isArray(conversationHistory)) this.conversationHistory = conversationHistory;
  }

  async getProfile() {
    const c = getCallable(URLS.getProfile);
    if (!c) return { success: false, error: 'not-ready' };
    try { return (await c({})).data; } catch (e) { return mapError(e); }
  }

  async grantConsent({ personalContext = {}, dataSharingScope = 'app-only' } = {}) {
    const c = getCallable(URLS.updateProfile);
    if (!c) return { success: false, error: 'not-ready' };
    try {
      return (await c({
        consent: { granted: true, retentionAccepted: true, dataSharingScope },
        personalContext,
      })).data;
    } catch (e) { return mapError(e); }
  }

  async deleteAll() {
    const c = getCallable(URLS.deleteProfile);
    if (!c) return { success: false, error: 'not-ready' };
    try {
      const r = (await c({ appId: APP_ID })).data;
      this.resetSession();
      return r;
    } catch (e) { return mapError(e); }
  }

  async exportData() {
    const c = getCallable(URLS.exportData);
    if (!c) return { success: false, error: 'not-ready' };
    try { return (await c({})).data; } catch (e) { return mapError(e); }
  }

  async startSession({ topic = null, sessionType = 'exploration' } = {}) {
    const c = getCallable(URLS.startSession);
    if (!c) return { success: false, error: 'not-ready' };
    try {
      const r = (await c({ appId: APP_ID, topic, sessionType })).data;
      if (r?.sessionId) this.sessionId = r.sessionId;
      this.conversationHistory = [];
      return r;
    } catch (e) { return mapError(e); }
  }

  async endSession({ rating = null } = {}) {
    if (!this.sessionId) return { success: true };
    const c = getCallable(URLS.endSession);
    if (!c) return { success: false, error: 'not-ready' };
    try {
      const r = (await c({
        sessionId: this.sessionId, appId: APP_ID, rating, transcript: this.conversationHistory,
      })).data;
      this.resetSession();
      return r;
    } catch (e) { return mapError(e); }
  }

  async askCoach(question, {
    mode = 'coaching', currentModule = null, moduleName = null, sectionName = null, returnUrl = null,
  } = {}) {
    const c = getCallable(URLS.askCoach);
    if (!c) {
      return { success: false, error: 'not-ready', response: 'Please sign in before we talk.' };
    }
    try {
      const r = (await c({
        question,
        appId: APP_ID,
        conversationHistory: this.conversationHistory,
        sessionId: this.sessionId,
        mode,
        returnUrl,
        userContext: {
          // The module the reader came from, so Sofia knows what they were
          // reading (rendered by the Knowledge Hub's module context).
          ...(currentModule ? { currentModule } : {}),
          ...(currentModule && moduleName ? { moduleName } : {}),
          ...(currentModule && sectionName ? { sectionName } : {}),
          positionsSummary: buildPositionsContext(),
        },
      })).data;
      if (r.success) {
        if (r.sessionId) this.sessionId = r.sessionId;
        this.conversationHistory.push(
          { role: 'user', content: question },
          { role: 'assistant', content: r.response },
        );
        if (this.conversationHistory.length > 30) {
          this.conversationHistory = this.conversationHistory.slice(-30);
        }
      }
      return r;
    } catch (e) { return mapError(e); }
  }

  async forgetSession(sessionId) {
    const c = getCallable(URLS.forgetSession);
    if (!c) return { success: false, error: 'not-ready' };
    try { return (await c({ sessionId })).data; } catch (e) { return mapError(e); }
  }
}

const companionService = new CompanionService();
export default companionService;
export { CompanionService, APP_ID };
